import { Check, Close } from "@mui/icons-material";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { mixpanel } from "common/analytics";
import { ThumbsDownIcon, ThumbsUpIcon } from "common/icons";
import { useTranslation } from "common/locales";
import { EventType, Field } from "model";
import { useEffect, useRef, useState } from "react";
import {
  createOneReading,
  deleteAllTilesForField,
  invalidateFieldData,
} from "services";

const FEEDBACK_STORAGE_KEY = "coverFeedbackDates";

function getTodayKey(): string {
  return new Date().toISOString().split("T")[0];
}

function hasGivenFeedbackToday(fieldGuid: string): boolean {
  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    if (!stored) return false;
    const data = JSON.parse(stored);
    return data[fieldGuid] === getTodayKey();
  } catch {
    return false;
  }
}

function markFeedbackGiven(fieldGuid: string): void {
  try {
    const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
    const data = stored ? JSON.parse(stored) : {};
    data[fieldGuid] = getTodayKey();
    localStorage.setItem(FEEDBACK_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Ignore storage errors
  }
}

type FeedbackState =
  | "initial"
  | "editing"
  | "submitted-ok"
  | "submitted-not-ok";

const bounceInAnimation = keyframes`
  0% { transform: scale(0) rotate(-45deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(10deg); }
  70% { transform: scale(0.9) rotate(-5deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const fadeOutAnimation = keyframes`
  0% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0.8); }
`;

const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.4); }
  50% { box-shadow: 0 0 0 8px rgba(34, 197, 94, 0); }
`;

const AnimatedIcon = styled("div")<{
  $animateIn?: boolean;
  $animateOut?: boolean;
}>(({ $animateIn, $animateOut }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  ...($animateIn && {
    animation: `${bounceInAnimation} 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) forwards`,
  }),
  ...($animateOut && {
    animation: `${fadeOutAnimation} 0.2s ease-out forwards`,
  }),
}));

const CelebrationContainer = styled("div")({
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const CheckCircle = styled("div", {
  shouldForwardProp: (prop) => prop !== "$animate",
})<{ $animate?: boolean }>(({ $animate }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "2px solid #22c55e",
  background: "transparent",
  ...($animate && {
    animation: `${pulseGlow} 1s ease-out`,
  }),
}));

const createSparkleAnimation = (tx: number, ty: number) => keyframes`
  0% {
    transform: translate(0, 0) scale(0);
    opacity: 1;
  }
  60% {
    opacity: 1;
  }
  100% {
    transform: translate(${tx}px, ${ty}px) scale(1.2);
    opacity: 0;
  }
`;

const SparkleBase = styled("div", {
  shouldForwardProp: (prop) => prop !== "$color",
})<{ $color: string }>(({ $color }) => ({
  position: "absolute",
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: $color,
}));

const Sparkle1 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(-20, -16)} 0.5s ease-out forwards`,
});

const Sparkle2 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(18, -20)} 0.5s ease-out 0.05s forwards`,
});

const Sparkle3 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(24, 8)} 0.5s ease-out 0.1s forwards`,
});

const Sparkle4 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(-24, 12)} 0.5s ease-out 0.15s forwards`,
});

const Sparkle5 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(8, 22)} 0.5s ease-out 0.08s forwards`,
});

const Sparkle6 = styled(SparkleBase)({
  animation: `${createSparkleAnimation(-14, 20)} 0.5s ease-out 0.12s forwards`,
});

interface Props {
  field: Field;
  overgrazed?: boolean;
}

export default function TotalCoverFeedback({
  field,
  overgrazed = false,
}: Props) {
  const [feedbackState, setFeedbackState] = useState<FeedbackState>("initial");
  const [alreadyGaveFeedback, setAlreadyGaveFeedback] = useState(false);
  const [coverValue, setCoverValue] = useState<number>(field.dryMatterNow);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);
  const [showResultIcon, setShowResultIcon] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    // Reset feedback state when field changes
    setAlreadyGaveFeedback(hasGivenFeedbackToday(field.guid));
    setFeedbackState("initial");
    setShowResultIcon(false);
    setIsAnimatingOut(false);
  }, [field.guid]);

  useEffect(() => {
    setCoverValue(field.dryMatterNow);
  }, [field.dryMatterNow]);

  useEffect(() => {
    if (feedbackState === "editing" && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [feedbackState]);

  const handleThumbsUp = () => {
    if (alreadyGaveFeedback) return;

    setIsAnimatingOut(true);

    setTimeout(async () => {
      setIsAnimatingOut(false);
      setShowResultIcon(true);
      setFeedbackState("submitted-ok");

      const curDate = new Date();
      const timeDiff = 5 * 60000;

      try {
        mixpanel.track("Cover Reading Submitted", {
          method: "confirm",
          direction: "up",
        });
        await createOneReading({
          guid: "new",
          manualDryMatterInput: {
            dryMatterValue: field.dryMatterNow,
            field: field.guid,
            dateTimeMeasurementStart: curDate.toISOString(),
            dateTimeMeasurementEnd: new Date(
              curDate.getTime() + timeDiff
            ).toISOString(),
            event: { eventType: EventType.None },
            kind: "user",
            verificationStatus: "Accepted",
          },
        });
        markFeedbackGiven(field.guid);
        setAlreadyGaveFeedback(true);
      } catch (e) {
        console.error("Failed to submit feedback", e);
      }
    }, 200);
  };

  const handleThumbsDown = () => {
    if (alreadyGaveFeedback) return;

    setFeedbackState("editing");
  };

  const handleSubmitCorrectedCover = () => {
    if (!coverValue) return;

    setIsAnimatingOut(true);

    setTimeout(async () => {
      setIsAnimatingOut(false);
      setShowResultIcon(true);
      setFeedbackState("submitted-not-ok");

      const curDate = new Date();
      const timeDiff = 5 * 60000;

      try {
        mixpanel.track("Cover Reading Submitted", {
          method: "adjust",
          direction: "down",
        });
        await createOneReading({
          guid: "new",
          manualDryMatterInput: {
            dryMatterValue: coverValue,
            field: field.guid,
            dateTimeMeasurementStart: curDate.toISOString(),
            dateTimeMeasurementEnd: new Date(
              curDate.getTime() + timeDiff
            ).toISOString(),
            event: { eventType: EventType.None },
            kind: "user",
            verificationStatus: "Accepted",
          },
        });

        await deleteAllTilesForField(field.guid);
        invalidateFieldData(field.guid);
        markFeedbackGiven(field.guid);
        setAlreadyGaveFeedback(true);
      } catch (e) {
        console.error("Failed to submit corrected cover", e);
      }
    }, 200);
  };

  const handleCancelEdit = () => {
    setFeedbackState("initial");
    setCoverValue(field.dryMatterNow);
    mixpanel.track("Cover Reading Cancelled", { direction: "down" });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmitCorrectedCover();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  // Render based on feedback state
  const renderContent = () => {
    if (
      feedbackState === "submitted-ok" ||
      feedbackState === "submitted-not-ok"
    ) {
      return (
        <>
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "#2E7831", display: "block", fontSize: "0.7rem" }}
            >
              {t("fieldDetail.totalCover")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {feedbackState === "submitted-not-ok"
                  ? coverValue
                  : field.dryMatterNow}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
              >
                kgDM/ha
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: "#2E7831", fontSize: "0.75rem" }}
            >
              {t("fieldDetail.coverFeedback.thankYou")}
            </Typography>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {showResultIcon && (
                <CelebrationContainer>
                  {/* Sparkle particles - only for thumbs up */}
                  {feedbackState === "submitted-ok" && (
                    <>
                      <Sparkle1 $color="#22C55E" />
                      <Sparkle2 $color="#86EFAC" />
                      <Sparkle3 $color="#4ADE80" />
                      <Sparkle4 $color="#BBF7D0" />
                      <Sparkle5 $color="#22C55E" />
                      <Sparkle6 $color="#86EFAC" />
                    </>
                  )}
                  <AnimatedIcon $animateIn>
                    <CheckCircle $animate={feedbackState === "submitted-ok"}>
                      <Check sx={{ color: "#22C55E", fontSize: 18 }} />
                    </CheckCircle>
                  </AnimatedIcon>
                </CelebrationContainer>
              )}
            </Box>
          </Box>
        </>
      );
    }

    if (feedbackState === "editing") {
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "#2E7831", display: "block", fontSize: "0.7rem" }}
              >
                {t("fieldDetail.totalCover")}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <TextField
                  inputRef={inputRef}
                  variant="standard"
                  value={coverValue ?? ""}
                  type="number"
                  onChange={(e) => setCoverValue(Number(e.target.value))}
                  onKeyDown={handleKeyDown}
                  sx={{ width: "auto" }}
                  slotProps={{
                    input: {
                      sx: {
                        fontWeight: 600,
                        color: "#1a1a2e",
                        fontSize: "1rem",
                        "& input": {
                          padding: 0,
                          width: "70px",
                          textAlign: "left",
                        },
                        "&:before": {
                          borderBottomColor: "#AFE57F",
                        },
                        "&:after": {
                          borderBottomColor: "#2E7831",
                        },
                      },
                    },
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
                >
                  kgDM/ha
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                onClick={handleCancelEdit}
                size="small"
                sx={{
                  color: "#9CA3AF",
                  "&:hover": {
                    backgroundColor: "rgba(156, 163, 175, 0.1)",
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
              <IconButton
                onClick={handleSubmitCorrectedCover}
                disabled={!coverValue}
                size="small"
                sx={{
                  color: "#2E7831",
                  "&:hover": {
                    backgroundColor: "rgba(28, 67, 33, 0.1)",
                  },
                  "&.Mui-disabled": {
                    color: "#9CA3AF",
                  },
                }}
              >
                <Check fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          <Box />
        </>
      );
    }

    // Initial state
    // Use warning colors when overgrazed
    const labelColor = overgrazed ? "#B45309" : "#2E7831"; // amber.700 vs green
    const iconColor = overgrazed ? "#B45309" : "#1C4321";
    const hoverBg = overgrazed
      ? "rgba(180, 83, 9, 0.1)"
      : "rgba(28, 67, 33, 0.1)";

    // If user already gave feedback (from localStorage), show thank you state
    if (alreadyGaveFeedback) {
      return (
        <>
          <Box>
            {overgrazed && (
              <Typography
                variant="caption"
                sx={{
                  color: "#B45309",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "block",
                  mb: 0.25,
                }}
              >
                {t("fieldview.overgrazed")}
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{ color: labelColor, display: "block", fontSize: "0.7rem" }}
            >
              {t("fieldDetail.totalCover")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {field && !field.invalidatedTimestamp
                  ? field.dryMatterNow
                  : "—"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
              >
                kgDM/ha
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="body2"
              sx={{ color: labelColor, fontSize: "0.75rem" }}
            >
              {t("fieldDetail.coverFeedback.thankYou")}
            </Typography>
            <CheckCircle>
              <Check sx={{ color: "#22C55E", fontSize: 18 }} />
            </CheckCircle>
          </Box>
        </>
      );
    }

    return (
      <>
        <Box>
          {overgrazed && (
            <Typography
              variant="caption"
              sx={{
                color: "#B45309",
                fontWeight: 600,
                fontSize: "0.65rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                display: "block",
                mb: 0.25,
              }}
            >
              {t("fieldview.overgrazed")}
            </Typography>
          )}
          <Typography
            variant="caption"
            sx={{ color: labelColor, display: "block", fontSize: "0.7rem" }}
          >
            {t("fieldDetail.totalCover")}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 600, color: "#1a1a2e" }}
            >
              {field && !field.invalidatedTimestamp ? field.dryMatterNow : "—"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#1a1a2e", fontSize: "0.7rem" }}
            >
              kgDM/ha
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: labelColor, fontSize: "0.75rem" }}
          >
            {t("fieldDetail.coverFeedback")}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <AnimatedIcon $animateOut={isAnimatingOut}>
              <IconButton
                onClick={handleThumbsUp}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: hoverBg,
                  },
                }}
              >
                <ThumbsUpIcon htmlColor={iconColor} fontSize="small" />
              </IconButton>
            </AnimatedIcon>
            <AnimatedIcon $animateOut={isAnimatingOut}>
              <IconButton
                onClick={handleThumbsDown}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: hoverBg,
                  },
                }}
              >
                <ThumbsDownIcon htmlColor={iconColor} fontSize="small" />
              </IconButton>
            </AnimatedIcon>
          </Box>
        </Box>
      </>
    );
  };

  // Daisy warning colors from theme
  const warningBg = "#FFFBEB"; // daisy.50 equivalent
  const warningBorder = "#F59E0B"; // daisy.500 equivalent
  const normalBg = "#F7FCF1";
  const normalBorder = "#AFE57F";

  return (
    <Box
      sx={{
        backgroundColor: overgrazed ? warningBg : normalBg,
        border: `1px solid ${overgrazed ? warningBorder : normalBorder}`,
        borderRadius: 2,
        px: 1.5,
        py: 1,
        mb: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minHeight: 56,
      }}
    >
      {renderContent()}
    </Box>
  );
}
